import { formatDate } from '@stern-ui/utilities';
import { createColumnHelper } from '@tanstack/react-table';

import { columnDefExtended, DataTableObject, TableConfigType } from '@/components';
import { screeningResultReasonOptions } from '@/globalConsts';

import { TrialSiteExclusionsObject } from '../types';

const columnHelper = createColumnHelper<DataTableObject>();

// Table configuration for the trial site exclusions list. This includes the columns to be displayed in the table, the default visibility of the columns and any custom cell rendering logic.
const trialSiteExclusionsColumns = [
	columnHelper.accessor(
		(row) =>
			`${(row as TrialSiteExclusionsObject).patient_first_name} ${(row as TrialSiteExclusionsObject).patient_last_name}`,
		{
			id: 'name',
			header: () => 'Patient name',
			isPii: true,
		} as columnDefExtended
	),
	columnHelper.accessor('identifier', {
		header: () => 'Study ID',
	}),
	columnHelper.accessor('study_name', {
		header: () => 'Study name',
	}),
	columnHelper.accessor('randomising_exclusion_reason', {
		header: () => 'Reason',
		cell: (props) => {
			if (!(props.row.original as TrialSiteExclusionsObject).randomising_exclusion_reason)
				return '-';
			const reasonKey = (props.row.original as TrialSiteExclusionsObject)
				.randomising_exclusion_reason;
			// reason is returned as a key, so we need to find the corresponding label to display to the user. If no label is found then display the key as a fallback
			const reason =
				screeningResultReasonOptions.find((option) => option.value === reasonKey)?.label ||
				reasonKey;
			const reasonNotes = (props.row.original as TrialSiteExclusionsObject)
				.randomising_exclusion_note;
			return reasonNotes ? `${reason}: ${reasonNotes}` : reason;
		},
	}),
	columnHelper.accessor('randomising_exclusion_date', {
		header: () => 'Date excluded',
		cell: (info) => {
			return formatDate(info.getValue() as string);
		},
		enableSorting: true,
	}),
	columnHelper.accessor('study_site_name', {
		header: () => 'Study site',
	}),
	columnHelper.accessor('provider_name', {
		header: () => 'HCI',
	}),
];

const defaultColumnVisibility = {
	name: false,
	study_code: true,
	study_name: true,
	randomising_exclusion_reason: true,
	randomising_exclusion_date: true,
	study_site_name: true,
	provider_name: true,
};

export const trialSiteExclusionsTableConfig: TableConfigType = {
	columns: trialSiteExclusionsColumns,
	rowProps: { rowSelect: false },
	visibleColumnDef: defaultColumnVisibility,
};
